/**
 * Phần "Tài khoản kết nối" trong chi tiết thiết bị (MH-03, tab Thông tin chung) —
 * cấp / cấp lại mật khẩu DV001, thu hồi kết nối, mở khoá cho thiết bị kết nối TRỰC
 * TIẾP. Thiết bị gián tiếp chỉ hiện dòng giải thích (xác thực qua bộ tương thích).
 * Chỉ hiện với người có quyền `device.credential` (kiểm ở MH-03).
 */
import { useState, type ReactNode } from 'react';
import { dienVao } from '../ngon-ngu/vi';
import { viNhaCungCap } from '../ngon-ngu/vi-nha-cung-cap';
import {
  LoiApi,
  nhaCungCapApi,
  type NhaCungCapApi,
  type TaiKhoanVuaCap,
  type TinhTrangTaiKhoanThietBi,
} from '../api/nha-cung-cap-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { HopXacNhan } from '../thanh-phan/HopXacNhan';
import { HopTaiKhoanVuaCap } from '../thanh-phan/HopTaiKhoanVuaCap';
import { TheTag } from '../thanh-phan/MauGiaoDien';
import './nha-cung-cap.css';

const t = viNhaCungCap.thietBi;
const thoiDiem = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('vi-VN', { hour12: false }) : '—';
const thongDiepLoi = (e: unknown): string =>
  e instanceof LoiApi || e instanceof Error ? e.message : viNhaCungCap.chung.thatBai;

export function TaiKhoanKetNoiThietBi({
  deviceId,
  cumLoaId,
  phuongThucKetNoi,
  api = nhaCungCapApi,
}: {
  deviceId: string;
  cumLoaId: string;
  phuongThucKetNoi: string;
  api?: NhaCungCapApi;
}): ReactNode {
  const [moc, datMoc] = useState(0);
  const [hoi, datHoi] = useState<'cap-lai' | 'thu-hoi' | 'kich-hoat-lai' | null>(null);
  const [lyDo, datLyDo] = useState('');
  const [taiKhoan, datTaiKhoan] = useState<TaiKhoanVuaCap | null>(null);
  const [thongBao, datThongBao] = useState<string | null>(null);
  const [dangGui, datDangGui] = useState(false);
  const trucTiep = phuongThucKetNoi === 'truc_tiep';

  // Thiết bị gián tiếp không có tài khoản riêng: không gọi API.
  const kq = dungTaiDuLieu<TinhTrangTaiKhoanThietBi | null>({
    tai: (signal) => (trucTiep ? api.tinhTrangThietBi(deviceId, signal) : Promise.resolve(null)),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [deviceId, moc, trucTiep],
  });
  const tt = kq.duLieu;

  const lam = async (viec: () => Promise<unknown>, xong?: string): Promise<void> => {
    datDangGui(true);
    datThongBao(null);
    try {
      await viec();
      if (xong) datThongBao(xong);
      datMoc((x) => x + 1);
    } catch (err) {
      datThongBao(thongDiepLoi(err));
    } finally {
      datDangGui(false);
    }
  };

  const cap = (): Promise<void> =>
    lam(async () => {
      const r = await api.capThietBi(deviceId);
      datTaiKhoan({ tenDangNhap: r.tenDangNhap, matKhau: r.matKhau, diaChiXacThuc: r.diaChiXacThuc });
    });

  if (!trucTiep) {
    return (
      <section className="tk-ket-noi" data-vung="tai-khoan-ket-noi">
        <h2>{t.tieuDe}</h2>
        <p>{t.gianTiep}</p>
      </section>
    );
  }

  const conHieuLuc = Boolean(tt?.coTaiKhoan && !tt.thoiDiemThuHoi);

  return (
    <section className="tk-ket-noi" data-vung="tai-khoan-ket-noi">
      <h2>{t.tieuDe}</h2>
      {kq.trangThai === 'loi' && kq.loi ? (
        <p className="ncc-loi" role="alert">
          {kq.loi.thongBao}
        </p>
      ) : null}
      {tt && !tt.coTaiKhoan ? <p>{t.chuaCo}</p> : null}
      {tt?.coTaiKhoan ? (
        <dl>
          <dt>{t.tenDangNhap}</dt>
          <dd>
            <code className="ncc-ma">{tt.tenDangNhap}</code>
          </dd>
          <dt>{t.thoiDiemCap}</dt>
          <dd>{thoiDiem(tt.thoiDiemCap)}</dd>
          {tt.thoiDiemThuHoi ? (
            <>
              <dt>{t.daThuHoi}</dt>
              <dd>
                <TheTag kieu="do">{thoiDiem(tt.thoiDiemThuHoi)}</TheTag>
              </dd>
            </>
          ) : null}
          {tt.biKhoaDen ? (
            <>
              <dt>{t.biKhoa}</dt>
              <dd>
                <TheTag kieu="vang">{thoiDiem(tt.biKhoaDen)}</TheTag>
              </dd>
            </>
          ) : null}
          <dt>{t.soToken}</dt>
          <dd>{tt.soTokenConHieuLuc}</dd>
        </dl>
      ) : null}
      {thongBao ? <p role="status">{thongBao}</p> : null}
      <div className="man-hinh__hanh-dong">
        {tt && !tt.coTaiKhoan ? (
          <button type="button" className="nut nut--chinh" disabled={dangGui} onClick={() => void cap()}>
            {t.cap}
          </button>
        ) : null}
        {tt?.coTaiKhoan ? (
          <button
            type="button"
            className="nut nut--chinh"
            disabled={dangGui}
            onClick={() => (conHieuLuc ? datHoi('cap-lai') : void cap())}
          >
            {t.capLai}
          </button>
        ) : null}
        {conHieuLuc ? (
          <button
            type="button"
            className="nut nut--nguy-hiem"
            disabled={dangGui}
            onClick={() => datHoi('thu-hoi')}
          >
            {t.thuHoi}
          </button>
        ) : null}
        {tt?.coTaiKhoan ? (
          <button
            type="button"
            className="nut"
            data-hanh-dong="kich-hoat-lai"
            disabled={dangGui}
            onClick={() => datHoi('kich-hoat-lai')}
          >
            {t.kichHoatLai}
          </button>
        ) : null}
        {tt?.biKhoaDen ? (
          <button
            type="button"
            className="nut"
            disabled={dangGui}
            onClick={() => void lam(() => api.moKhoaThietBi(deviceId), t.daMoKhoa)}
          >
            {t.moKhoa}
          </button>
        ) : null}
      </div>

      {hoi === 'cap-lai' ? (
        <HopXacNhan
          tieuDe={t.xacNhanCapLaiTieuDe}
          heQua={dienVao(t.xacNhanCapLai, { ma: cumLoaId, so: tt?.soTokenConHieuLuc ?? 0 })}
          nhanThucHien={t.capLai}
          onHuy={() => datHoi(null)}
          onThucHien={() => {
            datHoi(null);
            void cap();
          }}
        />
      ) : null}

      {hoi === 'kich-hoat-lai' ? (
        <HopXacNhan
          tieuDe={t.kichHoatLaiTieuDe}
          heQua={dienVao(t.kichHoatLaiMoTa, { ma: cumLoaId, so: tt?.soTokenConHieuLuc ?? 0 })}
          nhanThucHien={t.kichHoatLai}
          onHuy={() => datHoi(null)}
          onThucHien={() => {
            datHoi(null);
            void lam(() => api.kichHoatLaiThietBi(deviceId), t.daKichHoatLai);
          }}
        />
      ) : null}

      {hoi === 'thu-hoi' ? (
        <div className="man-che" role="presentation">
          <form
            className="hop-thoai ncc-bieu-mau"
            role="dialog"
            aria-modal="true"
            aria-label={t.thuHoiTieuDe}
            onSubmit={(e) => {
              e.preventDefault();
              datHoi(null);
              void lam(() => api.thuHoiThietBi(deviceId, lyDo.trim()), t.daThuHoiXong).then(() =>
                datLyDo(''),
              );
            }}
          >
            <h2>{t.thuHoiTieuDe}</h2>
            <p>{dienVao(t.thuHoiMoTa, { ma: cumLoaId, so: tt?.soTokenConHieuLuc ?? 0 })}</p>
            <label>
              {t.lyDo} <span className="ncc-bat-buoc">*</span>
              <textarea
                value={lyDo}
                onChange={(e) => datLyDo(e.target.value)}
                placeholder={t.lyDoGoiY}
                required
                minLength={5}
                maxLength={300}
              />
            </label>
            <div className="man-hinh__hanh-dong">
              <button type="button" className="nut" onClick={() => datHoi(null)}>
                {viNhaCungCap.chung.huy}
              </button>
              <button type="submit" className="nut nut--nguy-hiem" disabled={lyDo.trim().length < 5}>
                {t.thuHoi}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {taiKhoan ? <HopTaiKhoanVuaCap taiKhoan={taiKhoan} onDong={() => datTaiKhoan(null)} /> : null}
    </section>
  );
}
